import { Alert, Button, Empty, Modal, Tag, Typography } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { ERPTable, type ERPColumnDef } from '@/components/DataTable/ERPTable';
import { inventoryService } from '@/services/inventory.service';
import { ROUTES } from '@/constants/routes';

/**
 * TCR-2495 — bấm mã ERP ở danh sách Đơn hàng POS mở popup tồn kho CHỈ XEM của đúng mã đó,
 * cùng khung với popup bên Đơn Ecom (TCR-2459).
 *
 * Không dùng lại `ErpSkuStockModal` của Ecom: popup đó đọc API Tồn an toàn, chỉ có kho gán
 * ECom và các cột "giữ chỗ ECom / tồn gửi sàn" — vô nghĩa với đơn POS. Ở đây đọc `GET /inventory`
 * nhóm theo kho (cùng read-model màn Tồn kho), BE tự giới hạn kho theo phạm vi vai trò.
 */

const { Text } = Typography;

/** BE chặn page_size > 200; một mã chỉ trải trên vài chục kho nên 1 trang là đủ. */
const PAGE_SIZE = 200;

/** Phần của dòng `GET /inventory?group_by=variant_warehouse` mà popup cần. */
export interface PosSkuStockRow {
  _id: string;
  sku: string;
  product_name: string;
  warehouse_id: string;
  warehouse_name?: string;
  store_name?: string;
  unit_in_stock?: number;
  unit_reserved?: number;
  available_quantity?: number;
}

export function inventoryListUrl(sku: string): string {
  return `${ROUTES.inventory.list}?${new URLSearchParams({ search: sku }).toString()}`;
}

/** Kho xuất của đơn lên đầu, các kho còn lại giữ thứ tự BE trả. */
export function sortOrderWarehousesFirst(rows: PosSkuStockRow[], orderWarehouseIds: readonly string[]): PosSkuStockRow[] {
  const own = new Set(orderWarehouseIds);
  return [...rows.filter((row) => own.has(row.warehouse_id)), ...rows.filter((row) => !own.has(row.warehouse_id))];
}

function PosErpSkuStockContent({ sku, orderWarehouseIds }: { sku: string; orderWarehouseIds: readonly string[] }) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['inventory', 'pos-order-sku-stock', sku],
    queryFn: () => inventoryService.list({ search: sku, group_by: 'variant_warehouse', page: 1, page_size: PAGE_SIZE }),
    retry: false,
  });
  // Ô tìm của BE là tìm gần đúng (mã hoặc tên) ⇒ chỉ giữ dòng trùng khớp mã.
  const rows = sortOrderWarehousesFirst(
    ((data?.data ?? []) as unknown as PosSkuStockRow[]).filter((row) => row.sku === sku),
    orderWarehouseIds,
  );

  if (error) {
    const forbidden = isAxiosError(error) && error.response?.status === 403;
    return (
      <Alert
        type={forbidden ? 'warning' : 'error'}
        showIcon
        message={forbidden ? 'Bạn chưa có quyền xem tồn kho' : 'Không tải được tồn kho'}
        action={forbidden ? undefined : <Button size="small" onClick={() => refetch()}>Thử lại</Button>}
      />
    );
  }

  const own = new Set(orderWarehouseIds);
  const columns: ERPColumnDef<PosSkuStockRow>[] = [
    {
      key: 'warehouse_name',
      title: 'Kho',
      render: (_: unknown, row) => (
        <>
          {row.warehouse_name || row.warehouse_id}
          {own.has(row.warehouse_id) && <Tag color="blue" style={{ marginLeft: 8 }}>Kho xuất đơn này</Tag>}
        </>
      ),
    },
    { key: 'store_name', title: 'Chi nhánh', dataIndex: 'store_name', render: (value?: string) => value || '—' },
    { key: 'unit_in_stock', title: 'Tồn thực tế', dataIndex: 'unit_in_stock', align: 'right', render: (value?: number) => value ?? 0 },
    { key: 'unit_reserved', title: 'Đang giữ', dataIndex: 'unit_reserved', align: 'right', render: (value?: number) => value ?? 0 },
    {
      key: 'available_quantity', title: 'Khả dụng', dataIndex: 'available_quantity', align: 'right',
      render: (value?: number) => <Text strong>{value ?? 0}</Text>,
    },
  ];

  return (
    <>
      {rows[0]?.product_name && <div style={{ marginBottom: 12 }}><Text type="secondary">{rows[0].product_name}</Text></div>}
      <ERPTable<PosSkuStockRow>
        tableKey="orders-list-erp-sku-stock"
        hideSettingsButton
        size="small"
        rowKey="_id"
        columns={columns}
        dataSource={rows}
        loading={isLoading}
        pagination={false}
        scroll={{ y: 420 }}
        locale={{ emptyText: <Empty description="Mã này chưa có tồn ở kho nào trong phạm vi bạn được xem" /> }}
      />
    </>
  );
}

interface PosErpSkuStockModalProps {
  /** Mã ERP đang xem; `null` = đóng popup. */
  sku: string | null;
  /** Kho xuất của đơn chứa mã vừa bấm — đưa lên đầu và gắn nhãn. */
  orderWarehouseIds?: readonly string[];
  onClose: () => void;
}

export function PosErpSkuStockModal({ sku, orderWarehouseIds = [], onClose }: PosErpSkuStockModalProps) {
  return (
    <Modal
      title={sku ? `Tồn kho · ${sku}` : 'Tồn kho'}
      open={Boolean(sku)}
      onCancel={onClose}
      width={860}
      destroyOnHidden
      footer={sku ? [
        <Button key="inventory" icon={<ExportOutlined />} href={inventoryListUrl(sku)} target="_blank" rel="noopener noreferrer">
          Mở màn Tồn kho
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>Đóng</Button>,
      ] : null}
    >
      {sku && <PosErpSkuStockContent sku={sku} orderWarehouseIds={orderWarehouseIds} />}
    </Modal>
  );
}
